import type { KhoaDich } from '../i18n/types';

/**
 * Tùy chỉnh giao diện của người dùng (ADR-UI-0007).
 *
 * Người dùng đổi được **hai** thứ: màu nhấn và độ bo góc. Màu nhấn chọn từ 8 màu
 * có sẵn hoặc tự chọn bằng bộ chọn màu (ADR-UI-0016); màu tự chọn được tự làm
 * đậm cho tới khi chữ đọc được. Cố ý hẹp:
 *
 * - Màu nhấn và bán kính là hai thứ không phá được tương phản hay bố cục. Mọi
 *   bậc chữ, nền, viền vẫn do token của hệ quyết, nên bộ tùy chỉnh **không
 *   thể** tạo ra giao diện không đọc được — đó là lý do không cho sửa màu chữ
 *   hay màu nền.
 * - Thang màu trạng thái (`success`/`warning`/`danger`) **không** cho đổi:
 *   chúng mang nghĩa, không phải trang trí. Đổi `danger` sang xanh là làm sai
 *   thông tin, không phải làm đẹp.
 *
 * Lưu ở `localStorage`, mỗi người một máy. Chưa đồng bộ theo tài khoản vì
 * `openapi/platform-api.yaml` còn `paths: {}`; khi có API thì chỉ cần đổi
 * `docTuyChinh`/`luuTuyChinh`.
 */

import { chinhChoDocDuoc, chuanHoaHex, hexToRgb, laHex, pha } from '../lib/mau-sac';
import { thangBanKinh } from './tokens';

/** Khóa lưu. Phải khớp script chặn FOUC ở `index.html`. */
export const TUY_CHINH_STORAGE_KEY = 'foxai-tuy-chinh';

/**
 * Một màu nhấn chọn sẵn.
 *
 * `base` là bậc 600 — bậc dùng cho nút và mục điều hướng đang chọn. Các bậc
 * còn lại suy ra lúc chạy từ `base` (xem `apDungTuyChinh`).
 */
export interface MauNhan {
  /** Mã định danh lưu xuống localStorage. */
  ma: string;
  /** Khoá dịch của nhãn hiện trên giao diện. */
  khoa: KhoaDich;
  /** Bậc 600 của màu, dạng hex 6 ký tự. */
  base: string;
}

/**
 * Bảng màu nhấn chọn sẵn.
 *
 * Tất cả đã kiểm tương phản: chữ trắng trên `base` đạt ≥ 4.5:1, nên nút
 * `primary` của antd (chữ trắng trên nền `base`) đọc được ở mọi lựa chọn.
 * `scripts/check-design.mjs` kiểm lại con số này mỗi lần chạy `pnpm test`.
 */
export const MAU_NHAN: readonly MauNhan[] = [
  { ma: 'indigo', khoa: 'mau.indigo', base: '#4F46E5' },
  { ma: 'lam', khoa: 'mau.lam', base: '#1D4ED8' },
  { ma: 'luc-lam', khoa: 'mau.luc-lam', base: '#0E7490' },
  { ma: 'luc', khoa: 'mau.luc', base: '#047857' },
  { ma: 'tim', khoa: 'mau.tim', base: '#6D28D9' },
  { ma: 'hong', khoa: 'mau.hong', base: '#BE185D' },
  { ma: 'cam', khoa: 'mau.cam', base: '#C2410C' },
  { ma: 'than', khoa: 'mau.than', base: '#374151' },
] as const;

/** Một mức bo góc chọn sẵn. */
export interface MucBoGoc {
  ma: string;
  /** Khoá dịch của nhãn hiện trên giao diện. */
  khoa: KhoaDich;
  /** Bán kính gốc truyền cho antd; các bậc khác do antd suy ra. */
  px: number;
}

/**
 * Các mức bo góc.
 *
 * `6` là mặc định của antd (ADR-UI-0006). Hai mức còn lại vẫn để antd tự suy các
 * bậc phụ bằng `genRadius()`, nên không mức nào làm lệch góc giữa component
 * antd và class viết tay.
 */
export const MUC_BO_GOC: readonly MucBoGoc[] = [
  { ma: 'vuong', khoa: 'boGoc.vuong', px: 0 },
  { ma: 'nhe', khoa: 'boGoc.nhe', px: 4 },
  { ma: 'vua', khoa: 'boGoc.vua', px: 6 },
  /*
   * Mức mặc định: 12px là bán kính vai trò `control` mà §5.9 của
   * `UI_RECONSTRUCTION_SPEC.md` chốt, và §16.1 mục 1 xếp vào nhóm chặn. Người
   * dùng vẫn đổi được sang mức khác ở bảng tuỳ chỉnh — đây chỉ là điểm xuất
   * phát, để lần đầu mở ứng dụng là thấy đúng thiết kế của đặc tả.
   */
  { ma: 'dac-ta', khoa: 'boGoc.dac-ta', px: 12 },
  { ma: 'rat-tron', khoa: 'boGoc.rat-tron', px: 16 },
] as const;

/** `mauNhan` mang giá trị này khi người dùng dùng màu tự chọn ở `mauTuyY`. */
export const MA_MAU_TUY_Y = 'tuy-y';

export interface TuyChinh {
  /** `ma` của một phần tử trong `MAU_NHAN`, hoặc `MA_MAU_TUY_Y`. */
  mauNhan: string;
  /**
   * Màu tự chọn, dạng `#RRGGBB` in hoa. Giữ cả khi đang dùng màu chọn sẵn để
   * bộ chọn màu còn nhớ lần chọn trước; chỉ có hiệu lực khi `mauNhan` là
   * `MA_MAU_TUY_Y`.
   */
  mauTuyY?: string;
  /** `ma` của một phần tử trong `MUC_BO_GOC`. */
  boGoc: string;
}

export const TUY_CHINH_MAC_DINH: TuyChinh = { mauNhan: 'indigo', boGoc: 'dac-ta' };

export function timMauNhan(ma: string): MauNhan {
  return MAU_NHAN.find((m) => m.ma === ma) ?? MAU_NHAN[0];
}

/**
 * Màu nhấn đang dùng: bậc 600 thật sự áp lên giao diện.
 *
 * Màu tự chọn đi qua `chinhChoDocDuoc` nên `base` luôn đọc được; `daChinh` cho
 * giao diện biết để báo người dùng rằng màu đã bị làm đậm.
 */
export function layMauNhan(tc: TuyChinh): { base: string; daChinh: boolean } {
  if (tc.mauNhan === MA_MAU_TUY_Y && laHex(tc.mauTuyY)) {
    const { hex, daChinh } = chinhChoDocDuoc(tc.mauTuyY);
    return { base: hex, daChinh };
  }
  return { base: timMauNhan(tc.mauNhan).base, daChinh: false };
}

export function timMucBoGoc(ma: string): MucBoGoc {
  return MUC_BO_GOC.find((m) => m.ma === ma) ?? timMucBoGocMacDinh();
}

/** Mức bo góc mặc định, tra theo `ma` để không phụ thuộc thứ tự mảng. */
function timMucBoGocMacDinh(): MucBoGoc {
  return MUC_BO_GOC.find((m) => m.ma === TUY_CHINH_MAC_DINH.boGoc) ?? MUC_BO_GOC[0];
}

/* --------------------------------------------------------------------- */
/* Suy ra thang màu từ một bậc 600                                        */
/* --------------------------------------------------------------------- */

/**
 * Thang Indigo **nguyên văn §5.3** của `UI_RECONSTRUCTION_SPEC.md`.
 *
 * Màu nhấn mặc định phải khớp từng mã với đặc tả, không được là kết quả pha
 * gần đúng: §16.1 mục 2 xếp "primary là Indigo `#4F46E5`" vào nhóm chặn, và
 * `globals.css` cũng khai đúng bảng này. Pha lại từ bậc 600 cho ra `-700`
 * `#423bc0` thay vì `#4338CA` và `-50` `#f6f6fe` thay vì `#EEF2FF` — lệch đủ
 * để nhìn thấy trên nền nhãn và chữ của mục đang xem.
 *
 * Đặc tả ghi rõ: **không có bậc 950**.
 */
const THANG_INDIGO_DAC_TA: Record<string, string> = {
  50: '#EEF2FF',
  100: '#E0E7FF',
  200: '#C7D2FE',
  300: '#A5B4FC',
  400: '#818CF8',
  500: '#6366F1',
  600: '#4F46E5',
  700: '#4338CA',
  800: '#3730A3',
  900: '#312E81',
};

/**
 * Suy ra cả thang 50→900 từ bậc 600.
 *
 * Màu nhấn mặc định (Indigo) trả về đúng bảng của đặc tả. Bảy màu còn lại là
 * tuỳ chọn của người dùng, không có bảng trong đặc tả, nên vẫn suy bằng cách
 * pha với trắng/đen: nhẹ, chạy được mỗi lần đổi chủ đề, và tỉ lệ pha lấy từ
 * khoảng cách thực tế giữa các bậc của chính thang Indigo trên nên giữ đúng
 * "nhịp" của hệ thiết kế.
 */
export function thangMau(base: string): Record<string, string> {
  if (base.toUpperCase() === '#4F46E5') return { ...THANG_INDIGO_DAC_TA };

  return {
    50: pha(base, 0.95, 'trang'),
    100: pha(base, 0.88, 'trang'),
    200: pha(base, 0.74, 'trang'),
    300: pha(base, 0.56, 'trang'),
    400: pha(base, 0.32, 'trang'),
    500: pha(base, 0.14, 'trang'),
    600: base,
    700: pha(base, 0.16, 'den'),
    800: pha(base, 0.32, 'den'),
    900: pha(base, 0.46, 'den'),
  };
}

/* --------------------------------------------------------------------- */
/* Đọc / ghi / áp dụng                                                    */
/* --------------------------------------------------------------------- */

export function docTuyChinh(): TuyChinh {
  try {
    const raw = localStorage.getItem(TUY_CHINH_STORAGE_KEY);
    if (!raw) return TUY_CHINH_MAC_DINH;
    const parsed = JSON.parse(raw) as Partial<TuyChinh>;
    // Không tin dữ liệu đã lưu: khóa có thể là của bản cũ hoặc bị sửa tay.
    const mauTuyY = laHex(parsed.mauTuyY) ? chuanHoaHex(parsed.mauTuyY) : undefined;
    const dungMauTuyY = parsed.mauNhan === MA_MAU_TUY_Y && mauTuyY !== undefined;
    return {
      mauNhan: dungMauTuyY ? MA_MAU_TUY_Y : timMauNhan(parsed.mauNhan ?? '').ma,
      ...(mauTuyY ? { mauTuyY } : {}),
      boGoc: timMucBoGoc(parsed.boGoc ?? '').ma,
    };
  } catch {
    return TUY_CHINH_MAC_DINH;
  }
}

export function luuTuyChinh(tc: TuyChinh): void {
  try {
    localStorage.setItem(TUY_CHINH_STORAGE_KEY, JSON.stringify(tc));
  } catch {
    // Chế độ riêng tư: vẫn đổi được, chỉ không nhớ sang lần sau.
  }
}

/**
 * Ghi tùy chỉnh lên `<html>` dưới dạng biến CSS inline.
 *
 * Inline style thắng mọi selector nên không cần `!important`, và vì
 * `globals.css` đã trỏ mọi utility vào các biến này, một lệnh ghi là cả giao
 * diện đổi theo.
 *
 * ## Vì sao cần biết đang ở chủ đề tối
 *
 * Chủ đề sáng và chủ đề tối dùng thang primary **ngược chiều nhau**:
 *
 * | Bậc | Nền sáng | Nền tối |
 * | --- | --- | --- |
 * | `-50` (nền nhãn) | rất nhạt (`#EEF2FF`) | **trong suốt** `rgba(…, .16)` |
 * | `-600`/`-700` (chữ) | **đậm hơn** base | **nhạt hơn** base |
 *
 * Nếu ghi thang của nền sáng lên chủ đề tối thì chữ `-700` hóa ra tối hơn nền
 * → không đọc được. Vì vậy hàm này nhận `isDark` và đảo chiều, đúng cách
 * `.dark` trong `globals.css` đang làm.
 */
export function apDungTuyChinh(tc: TuyChinh, isDark: boolean): void {
  const root = document.documentElement;
  const { base } = layMauNhan(tc);
  const thang = thangMau(base);
  const set = (k: string, v: string) => root.style.setProperty(k, v);

  for (const [bac, gia] of Object.entries(thang)) {
    set(`--color-primary-${bac}`, gia);
  }

  if (isDark) {
    const [r, g, b] = hexToRgb(base);
    // Nền nhãn trong suốt, như cơ chế của `.dark` — để nền có màu không bị chói.
    set('--color-primary-50', `rgba(${r}, ${g}, ${b}, 0.16)`);
    set('--color-primary-100', `rgba(${r}, ${g}, ${b}, 0.26)`);
    /*
     * Bậc chữ sáng lên thay vì tối đi. Dùng thẳng bậc 400 và 300 của chính
     * thang đang áp, không pha lại: với Indigo mặc định thì ra đúng
     * `#818cf8` / `#a5b4fc` mà `.dark` trong `globals.css` khai, nên chủ đề
     * tối do người dùng chọn và chủ đề tối mặc định không lệch nhau.
     */
    set('--color-primary-600', thang[400]);
    set('--color-primary-700', thang[300]);
    set('--text-link', thang[300]);
  } else {
    set('--text-link', base);
  }

  set('--color-primary', base);

  // Bốn bậc bán kính, suy từ gốc bằng thuật toán của antd. Tailwind đọc đúng
  // bốn biến này nên class viết tay đổi cùng lúc với component antd.
  for (const [bien, gia] of Object.entries(thangBanKinh(timMucBoGoc(tc.boGoc).px))) {
    set(bien, gia);
  }
}
